import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, ChevronDown, ChevronUp, Download, Send,
  Loader2, Search, X, ChevronLeft, ChevronRight, SlidersHorizontal,
  Truck, CheckCircle2, AlertCircle, Circle, Eye,
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

const STATUS_OPTIONS = ['Новый', 'В обработке', 'Завершен', 'Отменен'];
const STATUS_TRANSITIONS = {
  'Новый':       ['В обработке', 'Отменен'],
  'В обработке': ['Завершен', 'Отменен'],
  'Завершен':    [],
  'Отменен':     [],
};
const SUPPLY_STATUS_OPTIONS = ['Открыта', 'Закрыта к изменениям', 'Накладная получена', 'Принята на склад', 'Закрыта', 'Отменена'];
const PAGE_SIZES = [10, 20, 50];

const STATUS_STYLE = {
  'Новый':       { badge: 'bg-blue-100 text-blue-800',   dot: 'bg-blue-500' },
  'В обработке': { badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  'Завершен':    { badge: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  'Отменен':     { badge: 'bg-red-100 text-red-800',     dot: 'bg-red-500' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatMoney = (n) =>
  n == null ? '0' : Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const INTERKOM_STATUS_PRIORITY = {
  'Нет в наличии': 4,
  'В обработке': 3,
  'В резерве': 3,
  'В отгрузке': 2,
};

const mergeInterkomStatus = (currentStatus, nextStatus) => {
  const current = String(currentStatus || '').trim();
  const next = String(nextStatus || '').trim();
  if (!current) return next;
  if (!next || current === next) return current;
  const currentPriority = INTERKOM_STATUS_PRIORITY[current] || 1;
  const nextPriority = INTERKOM_STATUS_PRIORITY[next] || 1;
  return currentPriority >= nextPriority ? current : next;
};

const interkomStatusClass = (status) => {
  const value = String(status || '').trim().toLowerCase();
  if (!value) return 'bg-gray-100 text-gray-500';
  if (value.includes('нет в наличии')) return 'bg-red-100 text-red-700';
  if (value.includes('отгруз')) return 'bg-slate-100 text-slate-700';
  if (value.includes('резерв') || value.includes('обработ')) return 'bg-amber-100 text-amber-800';
  return 'bg-blue-50 text-blue-700';
};

const isInterkomSupplier = (supplierName) =>
  String(supplierName || '').toLowerCase().includes('интерком');

const aggregateSupplyItems = (orders = []) => {
  const map = new Map();
  for (const order of orders) {
    for (const item of (order.items || [])) {
      const key = item.product_id || item.product_code || item.product_name;
      if (!key) continue;
      const quantity = Number(item.quantity || 0);
      const supplierPrice = Number(item.supplier_price || 0);
      const supplierSubtotal = Number(item.supplier_subtotal ?? supplierPrice * quantity);
      const current = map.get(key);
      if (!current) {
        map.set(key, {
          product_id: item.product_id,
          product_code: item.product_code,
          product_name: item.product_name,
          supplier_currency: item.supplier_currency,
          quantity,
          supplier_subtotal: supplierSubtotal,
          interkom_status: item.interkom_status || '',
          for_client: Boolean(item.for_client),
          client_names: item.client_name ? [item.client_name] : [],
          customer_order_numbers: item.customer_order_number ? [item.customer_order_number] : [],
        });
      } else {
        current.quantity += quantity;
        current.supplier_subtotal += supplierSubtotal;
        current.interkom_status = mergeInterkomStatus(current.interkom_status, item.interkom_status);
        current.for_client = current.for_client || Boolean(item.for_client);
        if (item.client_name && !current.client_names.includes(item.client_name)) {
          current.client_names.push(item.client_name);
        }
        if (item.customer_order_number && !current.customer_order_numbers.includes(item.customer_order_number)) {
          current.customer_order_numbers.push(item.customer_order_number);
        }
      }
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    String(a.product_name || '').localeCompare(String(b.product_name || ''), 'ru'),
  );
};

const buildDisplayRows = (orders = []) => {
  const supplyMap = new Map();
  const standalone = [];

  for (const order of orders) {
    if (!order?.supply_id) {
      standalone.push({ type: 'order', order, sort_at: order.created_at });
      continue;
    }
    const existing = supplyMap.get(order.supply_id);
    if (!existing) {
      supplyMap.set(order.supply_id, {
        type: 'supply',
        key: `supply-${order.supply_id}`,
        supply_id: order.supply_id,
        supply_number: order.supply_number,
        supply_status: order.supply_status,
        invoice_match_status: order.invoice_match_status || null,
        supplier_name: order.supplier_name,
        supplier_currency: order.supplier_currency,
        user_name: order.user_name,
        user_email: order.user_email,
        orders: [order],
        sort_at: order.created_at,
      });
    } else {
      existing.orders.push(order);
      if (new Date(order.created_at).getTime() > new Date(existing.sort_at).getTime()) {
        existing.sort_at = order.created_at;
      }
    }
  }

  const grouped = Array.from(supplyMap.values()).map((group) => {
    const statuses = Array.from(new Set(group.orders.map((o) => o.status).filter(Boolean)));
    const status = statuses.length === 1 ? statuses[0] : 'Смешанный';
    const totalSupplierAmount = group.orders.reduce((sum, o) => sum + Number(o.total_supplier_amount || 0), 0);
    const positionsCount = group.orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);
    const items = aggregateSupplyItems(group.orders);
    const totalQty = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const interkomOutOfStockCount = isInterkomSupplier(group.supplier_name)
      ? items.filter((item) => String(item.interkom_status || '').trim() === 'Нет в наличии').length
      : 0;
    return {
      ...group,
      status,
      total_supplier_amount: totalSupplierAmount,
      positions_count: positionsCount,
      items,
      total_qty: totalQty,
      interkom_out_of_stock_count: interkomOutOfStockCount,
    };
  });

  return [...grouped, ...standalone].sort(
    (a, b) => new Date(b.sort_at).getTime() - new Date(a.sort_at).getTime(),
  );
};

const STATUS_STYLE_INVOICE = {
  'Совпадает':        { cls: 'bg-green-100 text-green-800', Icon: CheckCircle2 },
  'Частично':         { cls: 'bg-amber-100 text-amber-800', Icon: AlertCircle },
  'Расхождение цен':  { cls: 'bg-orange-100 text-orange-800', Icon: AlertCircle },
  'Нет данных':       { cls: 'bg-gray-100 text-gray-500', Icon: Circle },
};

const InvoiceMatchBadge = ({ status }) => {
  if (!status) return null;
  const s = STATUS_STYLE_INVOICE[status] || { cls: 'bg-gray-100 text-gray-500', Icon: Circle };
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${s.cls}`}>
      <Icon className="w-3 h-3 flex-shrink-0" />
      {status}
    </span>
  );
};

const MATCH_STATUS_STYLE = {
  match:                  { row: '', badge: 'bg-green-100 text-green-700', label: 'Совпадает' },
  price_mismatch:         { row: 'bg-orange-50', badge: 'bg-orange-100 text-orange-700', label: 'Расхождение цены' },
  short:                  { row: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', label: 'Недостача' },
  short_price_mismatch:   { row: 'bg-amber-50', badge: 'bg-orange-100 text-orange-700', label: 'Недостача + цена' },
  extra:                  { row: 'bg-blue-50',  badge: 'bg-blue-100 text-blue-700',   label: 'Излишек' },
  extra_price_mismatch:   { row: 'bg-blue-50',  badge: 'bg-orange-100 text-orange-700', label: 'Излишек + цена' },
  missing:                { row: 'bg-red-50',   badge: 'bg-red-100 text-red-700',     label: 'Не отгружено' },
  unexpected:             { row: 'bg-purple-50',badge: 'bg-purple-100 text-purple-700', label: 'Не в заказе' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${s.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
      {status}
    </span>
  );
};

const SupplyGroupCard = ({
  group,
  isAdmin,
  onStatusChange,
  onSupplyStatusChange,
  onSendInterkom,
  sendingId,
  updatingSupplyId,
  token,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [supplyDetail, setSupplyDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [matchError, setMatchError] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);
  const isInterkom = isInterkomSupplier(group.supplier_name);
  const currency = group.supplier_currency || group.orders?.[0]?.supplier_currency || '₸';
  const isUpdatingSupply = updatingSupplyId === group.supply_id;

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !supplyDetail && group.supply_id && group.invoice_match_status) {
      setLoadingDetail(true);
      try {
        const { data } = await axios.get(`${API}/admin/supplies/${group.supply_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSupplyDetail(data);
      } catch {
        // silent — match table just won't show
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  const handleAutoMatch = async () => {
    setAutoMatching(true);
    setMatchError(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/supplies/${group.supply_id}/auto-match-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSupplyDetail(data.supply || null);
      // Update the group's invoice_match_status optimistically via parent reload is preferred,
      // but we force-show the match section here via supplyDetail
    } catch (err) {
      const msg = err.response?.data?.detail || 'Ошибка сверки';
      setMatchError(msg);
      toast.error(msg);
    } finally {
      setAutoMatching(false);
    }
  };

  return (
    <>
      <div
        className={`bg-white rounded-xl border transition-all duration-150 overflow-hidden ${
          expanded
            ? 'border-indigo-200 shadow-md'
            : 'border-gray-200 shadow-sm hover:border-gray-300 hover:shadow-md'
        }`}
      >
      <div
        className="px-5 py-4 cursor-pointer select-none"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
              expanded ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'
            }`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>

          <div className="w-52 flex-shrink-0 min-w-0">
            <p className="font-mono font-bold text-gray-900 text-sm truncate">
              Поставка {group.supply_number || group.supply_id}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{formatDate(group.sort_at)}</p>
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{group.supplier_name}</p>
            {isAdmin && (
              <p className="text-xs text-gray-400 truncate">{group.user_name} · {group.user_email}</p>
            )}
            <p className="text-xs text-indigo-500 truncate mt-0.5">
              {group.orders.length} заказов объединено · {group.supply_status || '—'}
            </p>
          </div>

          <div className="hidden sm:flex items-center gap-5 flex-shrink-0">
            <div className="text-center">
              <p className="text-sm font-bold text-gray-900">{group.positions_count}</p>
              <p className="text-[10px] uppercase tracking-wide text-gray-400">позиций</p>
            </div>
            <div className="text-right min-w-[100px]">
              <p className="text-sm font-bold text-gray-900">
                {formatMoney(group.total_supplier_amount)}&nbsp;{currency}
              </p>
              <p className="text-[10px] uppercase tracking-wide text-gray-400">закупочная</p>
            </div>
          </div>

          <div className="flex-shrink-0 flex items-center gap-2">
            {group.invoice_match_status && (
              <InvoiceMatchBadge status={group.invoice_match_status} />
            )}
            {isInterkom && group.interkom_out_of_stock_count > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 whitespace-nowrap">
                Нет в наличии: {group.interkom_out_of_stock_count}
              </span>
            )}
            <StatusBadge status={group.status} />
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100">
          {isAdmin && (
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Статус поставки:
                </span>
                <Select
                  value={group.supply_status || 'Открыта'}
                  onValueChange={(value) => onSupplyStatusChange(group.supply_id, value)}
                  disabled={isUpdatingSupply}
                >
                  <SelectTrigger className="h-8 w-56 text-sm border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPLY_STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isInterkom && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  disabled={autoMatching}
                  onClick={handleAutoMatch}
                >
                  {autoMatching ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Truck className="w-3.5 h-3.5" />
                  )}
                  {autoMatching ? 'Сверяем...' : 'Сверить с накладной'}
                </Button>
              )}
            </div>
          )}

          <div className="px-5 pt-4 pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">
              Сводный состав поставки
            </p>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {(isInterkom
                      ? ['Артикул', 'Наименование', 'Кол-во', 'Сумма', 'Статус Интерком']
                      : ['Артикул', 'Наименование', 'Кол-во', 'Сумма']
                    ).map((h, i) => (
                      <th
                        key={h}
                        className={`px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide ${
                          i <= 1 ? 'text-left' : i === 2 ? 'text-center' : isInterkom && i === 4 ? 'text-center' : 'text-right'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.items.map((item, idx) => (
                    <tr key={`${item.product_id || item.product_code || item.product_name}-${idx}`} className="hover:bg-indigo-50/30 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{item.product_code || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-900">
                        <div>{item.product_name}</div>
                        {item.for_client && (
                          <div className="mt-1 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                            Под клиента
                            {item.client_names?.length ? ` · ${item.client_names.join(', ')}` : ''}
                            {item.customer_order_numbers?.length ? ` · ${item.customer_order_numbers.join(', ')}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center font-semibold text-gray-800">{item.quantity}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-gray-900">{formatMoney(item.supplier_subtotal)}&nbsp;{item.supplier_currency || currency}</td>
                      {isInterkom && (
                        <td className="px-4 py-2.5 text-center">
                          {item.interkom_status ? (
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${interkomStatusClass(item.interkom_status)}`}>
                              {item.interkom_status}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-200">
                    <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-700">Итого</td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-gray-700">{group.total_qty}&nbsp;шт</td>
                    <td className="px-4 py-3 text-right text-base font-extrabold text-indigo-700 whitespace-nowrap">
                      {formatMoney(group.total_supplier_amount)}&nbsp;{currency}
                    </td>
                    {isInterkom && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* ── Invoice match section ──────────────────────────────────── */}
          {(group.invoice_match_status || supplyDetail?.invoice_match_status) && (
            <div className="px-5 pb-4">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Truck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  Сверка с накладной
                </p>
                <InvoiceMatchBadge status={supplyDetail?.invoice_match_status || group.invoice_match_status} />
                {supplyDetail?.invoice_match_document && (
                  <span className="text-xs text-gray-500 truncate">
                    · {supplyDetail.invoice_match_document}
                  </span>
                )}
              </div>
              {loadingDetail ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Загрузка...
                </div>
              ) : supplyDetail?.invoice_match_items?.length > 0 ? (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Артикул</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Наименование</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Заказано</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Отгружено</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Цена заказ</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Цена накл.</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Статус</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {supplyDetail.invoice_match_items.map((item, idx) => {
                        const ms = MATCH_STATUS_STYLE[item.status] || MATCH_STATUS_STYLE.missing;
                        const hasPriceMismatch = item.status?.includes('price_mismatch');
                        return (
                          <tr key={`${item.product_code}-${idx}`} className={`${ms.row} transition-colors`}>
                            <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{item.product_code || '—'}</td>
                            <td className="px-4 py-2.5 text-gray-900 text-xs">{item.product_name}</td>
                            <td className="px-4 py-2.5 text-center font-semibold text-gray-700">
                              {item.ordered_qty > 0 ? item.ordered_qty : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-center font-semibold text-gray-700">
                              {item.shipped_qty > 0 ? item.shipped_qty : '—'}
                            </td>
                            <td className={`px-4 py-2.5 text-center text-xs ${hasPriceMismatch ? 'font-semibold text-orange-700' : 'text-gray-600'}`}>
                              {item.ordered_price != null ? item.ordered_price.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                            </td>
                            <td className={`px-4 py-2.5 text-center text-xs ${hasPriceMismatch ? 'font-semibold text-orange-700' : 'text-gray-600'}`}>
                              {item.invoice_price != null ? item.invoice_price.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold ${ms.badge}`}>
                                {ms.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : !loadingDetail && (
                <p className="text-xs text-gray-400 py-2">Данные сверки недоступны</p>
              )}
            </div>
          )}

          <div className="px-5 pb-4">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">
              Заказы внутри поставки
            </p>
            <div className="space-y-2">
              {group.orders.map((order) => {
                const isSending = sendingId === order.id;
                return (
                  <div key={order.id} className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2 justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          #{order.order_number || order.id.substring(0, 10)}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">
                          {order.items?.length || 0} поз. · {formatMoney(order.total_supplier_amount)} {order.supplier_currency || currency}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs gap-1.5 bg-white"
                          onClick={() => setPreviewOrder(order)}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Просмотр
                        </Button>
                        <StatusBadge status={order.status} />
                        {isAdmin && (
                          <Select
                            value={order.status}
                            onValueChange={(value) => onStatusChange(order.id, value)}
                          >
                            <SelectTrigger className="h-8 w-40 text-sm border-gray-300 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {isAdmin && isInterkom && order.status === 'Новый' && (
                          <Button
                            size="sm"
                            className="h-8 text-xs gap-1.5"
                            disabled={isSending}
                            onClick={() => onSendInterkom(order.id)}
                          >
                            {isSending ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Send className="w-3.5 h-3.5" />
                            )}
                            {isSending ? 'Отправка...' : 'Отправить'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      </div>

      <Dialog open={Boolean(previewOrder)} onOpenChange={(open) => !open && setPreviewOrder(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Заказ #{previewOrder?.order_number || previewOrder?.id?.substring(0, 10) || '—'}
            </DialogTitle>
          </DialogHeader>

          {previewOrder && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                {formatDate(previewOrder.created_at)} · {previewOrder.items?.length || 0} поз. · {formatMoney(previewOrder.total_supplier_amount)} {previewOrder.supplier_currency || currency}
              </p>

              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {(isInterkom
                        ? ['Артикул', 'Наименование', 'Кол-во', 'Цена', 'Сумма', 'Статус Интерком']
                        : ['Артикул', 'Наименование', 'Кол-во', 'Цена', 'Сумма']
                      ).map((h, i) => (
                        <th
                          key={h}
                          className={`px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide ${
                            i === 0 ? 'text-left' :
                            i === 1 ? 'text-left' :
                            i === 2 ? 'text-center' :
                            isInterkom && i === 5 ? 'text-center' : 'text-right'
                          }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(previewOrder.items || []).map((item, idx) => {
                      const itemCurrency = item.supplier_currency || previewOrder.supplier_currency || currency;
                      const price = Number(item.supplier_price || 0);
                      const qty = Number(item.quantity || 0);
                      const subtotal = Number(item.supplier_subtotal ?? price * qty);
                      return (
                        <tr key={`${item.product_id || idx}-${item.customer_order_item_id || idx}`}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{item.product_code || '—'}</td>
                          <td className="px-3 py-2 text-gray-900">{item.product_name}</td>
                          <td className="px-3 py-2 text-center font-semibold text-gray-800">{qty}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatMoney(price)}&nbsp;{itemCurrency}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatMoney(subtotal)}&nbsp;{itemCurrency}</td>
                          {isInterkom && (
                            <td className="px-3 py-2 text-center">
                              {item.interkom_status ? (
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${interkomStatusClass(item.interkom_status)}`}>
                                  {item.interkom_status}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ── Single order card (accordion) ─────────────────────────────────────── */
const OrderCard = ({ order, isAdmin, onStatusChange, onSendInterkom, onExportCsv, sendingId }) => {
  const [expanded, setExpanded] = useState(false);

  const isInterkom = isInterkomSupplier(order.supplier_name);
  const currency   = order.supplier_currency || order.items?.[0]?.supplier_currency || '₸';
  const totalQty   = order.items?.reduce((s, i) => s + i.quantity, 0) || 0;
  const isSending  = sendingId === order.id;

  return (
    <div
      className={`bg-white rounded-xl border transition-all duration-150 overflow-hidden ${
        expanded
          ? 'border-blue-200 shadow-md'
          : 'border-gray-200 shadow-sm hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {/* ── Collapsed header ─────────────────────────────────────────────── */}
      <div
        className="px-5 py-4 cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
              expanded ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
            }`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>

          <div className="w-44 flex-shrink-0 min-w-0">
            <p className="font-mono font-bold text-gray-900 text-sm truncate">
              #{order.order_number || order.id.substring(0, 10)}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{formatDate(order.created_at)}</p>
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{order.supplier_name}</p>
            {isAdmin && (
              <p className="text-xs text-gray-400 truncate">{order.user_name} · {order.user_email}</p>
            )}
            {order.supply_number && (
              <p className="text-xs text-indigo-500 truncate mt-0.5">
                Поставка {order.supply_number}
                {order.supply_status ? ` · ${order.supply_status}` : ''}
              </p>
            )}
            {isInterkom && order.interkom_order_number && (
              <p className="text-xs text-slate-500 truncate mt-0.5">
                Интерком {order.interkom_order_number}
                {order.interkom_order_status ? ` · ${order.interkom_order_status}` : ''}
              </p>
            )}
          </div>

          <div className="hidden sm:flex items-center gap-5 flex-shrink-0">
            <div className="text-center">
              <p className="text-sm font-bold text-gray-900">{order.items?.length || 0}</p>
              <p className="text-[10px] uppercase tracking-wide text-gray-400">позиций</p>
            </div>
            <div className="text-right min-w-[100px]">
              <p className="text-sm font-bold text-gray-900">
                {formatMoney(order.total_supplier_amount)}&nbsp;{currency}
              </p>
              <p className="text-[10px] uppercase tracking-wide text-gray-400">закупочная</p>
            </div>
          </div>

          <div className="flex-shrink-0">
            <StatusBadge status={order.status} />
          </div>
        </div>
      </div>

      {/* ── Expanded body ────────────────────────────────────────────────── */}
      {expanded && (
        <div className="border-t border-gray-100">
          {/* Top action bar: status + interkom */}
          {isAdmin && (
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Статус:
                </span>
                <Select
                  value={order.status}
                  onValueChange={(value) => onStatusChange(order.id, value)}
                >
                  <SelectTrigger className="h-8 w-40 text-sm border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={order.status}>{order.status}</SelectItem>
                    {(STATUS_TRANSITIONS[order.status] || []).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isInterkom && order.status === 'Новый' && (
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  disabled={isSending}
                  onClick={() => onSendInterkom(order.id)}
                >
                  {isSending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  {isSending ? 'Отправка...' : 'Отправить в Интерком'}
                </Button>
              )}
            </div>
          )}

          {/* Items table */}
          <div className="px-5 pt-4 pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">
              Состав заказа
            </p>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {(isInterkom
                      ? ['Артикул', 'Наименование', 'Кол-во', 'Цена', 'Сумма', 'Статус Интерком']
                      : ['Артикул', 'Наименование', 'Кол-во', 'Цена', 'Сумма']
                    ).map((h, i) => (
                      <th
                        key={h}
                        className={`px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide ${
                          i === 0 ? 'text-left' :
                          i === 1 ? 'text-left' :
                          i === 2 ? 'text-center' :
                          isInterkom && i === 5 ? 'text-center' : 'text-right'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(order.items || []).map((item, idx) => {
                    const c        = item.supplier_currency || currency;
                    const price    = item.supplier_price || 0;
                    const subtotal = item.supplier_subtotal ?? price * item.quantity;
                    return (
                      <tr key={`${item.product_id || idx}-${item.customer_order_item_id || idx}`} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{item.product_code || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-900">
                          <div>{item.product_name}</div>
                          {item.for_client && (
                            <div className="mt-0.5 text-[11px] font-medium text-indigo-600">
                              Под клиента
                              {item.client_name ? ` · ${item.client_name}` : ''}
                              {item.customer_order_number ? ` · ${item.customer_order_number}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center font-semibold text-gray-800">{item.quantity}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{formatMoney(price)}&nbsp;{c}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-gray-900">{formatMoney(subtotal)}&nbsp;{c}</td>
                        {isInterkom && (
                          <td className="px-4 py-2.5 text-center">
                            {item.interkom_status ? (
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${interkomStatusClass(item.interkom_status)}`}>
                                {item.interkom_status}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-200">
                    <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-700">Итого</td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-gray-700">{totalQty}&nbsp;шт</td>
                    <td />
                    <td className="px-4 py-3 text-right text-base font-extrabold text-blue-700">
                      {formatMoney(order.total_supplier_amount)}&nbsp;{currency}
                    </td>
                    {isInterkom && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Bottom: CSV export */}
          {!isInterkom && (
            <div className="px-5 py-3 bg-gray-50/70 border-t border-gray-100 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5"
                onClick={() => onExportCsv(order)}
              >
                <Download className="w-3.5 h-3.5" />
                Скачать CSV
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Pagination controls ────────────────────────────────────────────────── */
const Pagination = ({ page, pageSize, total, onPage, onPageSize }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  const pages = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
      pages.push(i);
    }
  }
  // Insert ellipsis markers
  const withEllipsis = [];
  let prev = null;
  for (const p of pages) {
    if (prev !== null && p - prev > 1) withEllipsis.push('…');
    withEllipsis.push(p);
    prev = p;
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-4">
      {/* Info */}
      <p className="text-sm text-gray-500">
        {total === 0 ? 'Нет заказов' : `${from}–${to} из ${total}`}
      </p>

      {/* Pages */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {withEllipsis.map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'default' : 'outline'}
              size="sm"
              className="h-8 w-8 p-0 text-xs"
              onClick={() => onPage(p)}
            >
              {p}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={page === totalPages}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Page size */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">На странице:</span>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSize(Number(v))}>
          <SelectTrigger className="h-8 w-16 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((s) => (
              <SelectItem key={s} value={String(s)}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────── */
const EMPTY_FILTERS = { search: '', product_search: '', status: '', supplier_id: '', date_from: '', date_to: '', invoice_filter: '' };

const OrdersPage = () => {
  const { user, token } = useAuth();
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [updatingSupplyId, setUpdatingSupplyId] = useState(null);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(20);
  const [filters, setFilters]     = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput]               = useState('');
  const [productSearchInput, setProductSearchInput] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [bulkMatching, setBulkMatching] = useState(false);
  const [bulkResult, setBulkResult]     = useState(null);
  const displayRows = useMemo(() => buildDisplayRows(orders), [orders]);
  const highlightedOrderRef = useRef(null);

  // Pre-fill search from URL ?q= param (e.g. when navigating from "В заказе" badge)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) {
      setSearchInput(q);
      setFilters((f) => ({ ...f, search: q }));
    }
  }, []);

  // Debounced search: update filters.search 400 ms after typing stops
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchInput }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Debounced product search
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, product_search: productSearchInput }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [productSearchInput]);

  // Reset page when filters (except search, handled above) change
  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setProductSearchInput('');
    setPage(1);
  };

  const hasActiveFilters = filters.search || filters.product_search || filters.status || filters.supplier_id || filters.date_from || filters.date_to || filters.invoice_filter;

  const fetchOrders = useCallback(async () => {
    if (!user || !token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (filters.search)          params.set('search',         filters.search);
      if (filters.product_search)  params.set('product_search', filters.product_search);
      if (filters.status)          params.set('status',         filters.status);
      if (filters.supplier_id)     params.set('supplier_id',    filters.supplier_id);
      if (filters.date_from)       params.set('date_from',      filters.date_from);
      if (filters.date_to)         params.set('date_to',        filters.date_to);
      if (filters.invoice_filter)  params.set('invoice_filter', filters.invoice_filter);

      const { data } = await axios.get(`${API}/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(data.orders);
      setTotal(data.total);
    } catch {
      toast.error('Ошибка загрузки заказов');
    } finally {
      setLoading(false);
    }
  }, [user, token, page, pageSize, filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Load suppliers list for admin filter
  useEffect(() => {
    if (!user?.is_admin || !token) return;
    axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setSuppliers(data.suppliers || data || []))
      .catch(() => {});
  }, [user, token]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const { data } = await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedOrder = data?.order;
      setOrders((prev) => prev.map((o) => {
        if (o.id !== orderId) return o;
        if (updatedOrder) return updatedOrder;
        return { ...o, status: newStatus };
      }));
      toast.success('Статус обновлён');
    } catch {
      toast.error('Ошибка обновления статуса');
    }
  };

  const handleSendInterkom = async (orderId) => {
    setSendingId(orderId);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${orderId}/send-interkom`, {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sent   = data?.sent   || 0;
      const failed = data?.failed || 0;
      if      (sent > 0 && failed === 0) toast.success(`Отправлено: ${sent} позиций`);
      else if (sent > 0)                 toast.warning(`Частично: ${sent} ОК, ${failed} ошибок`);
      else                               toast.error(`Ошибка отправки (${failed} позиций)`);

      if (data?.order) {
        setOrders((prev) => prev.map((o) => (o.id === orderId ? data.order : o)));
      } else if (data?.status_updated) {
        setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: 'В обработке' } : o)));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки в Интерком');
    } finally {
      setSendingId(null);
    }
  };

  const handleSupplyStatusChange = async (supplyId, newStatus) => {
    if (!supplyId) return;
    setUpdatingSupplyId(supplyId);
    try {
      const { data } = await axios.put(
        `${API}/admin/supplies/${supplyId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders((prev) => prev.map((o) => (
        o.supply_id === supplyId
          ? {
              ...o,
              supply_status: newStatus,
              ...(newStatus === 'Закрыта' ? { status: 'Завершен' } : {}),
            }
          : o
      )));
      const completed = Number(data?.orders_completed || 0);
      if (newStatus === 'Закрыта' && completed > 0) {
        toast.success(`Поставка закрыта, заказов завершено: ${completed}`);
      } else {
        toast.success('Статус поставки обновлён');
      }
    } catch {
      toast.error('Ошибка обновления статуса поставки');
    } finally {
      setUpdatingSupplyId(null);
    }
  };

  const handleExportCsv = (order) => {
    const currency = order.supplier_currency || order.items?.[0]?.supplier_currency || '₸';
    const header   = ['Дата заказа', 'Номер заказа', 'Артикул', 'Наименование', 'Кол-во', 'Цена', 'Сумма', 'Валюта'];
    const rows     = [
      header,
      ...(order.items || []).map((item) => {
        const price    = item.supplier_price ?? 0;
        const qty      = item.quantity ?? 0;
        const subtotal = item.supplier_subtotal ?? price * qty;
        return [formatDate(order.created_at), order.order_number || order.id, item.product_code || '', item.product_name || '', qty, price, subtotal, item.supplier_currency || currency];
      }),
    ];
    const escape = (f) => { const s = String(f ?? ''); return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv    = rows.map((r) => r.map(escape).join(';')).join('\n');
    const blob   = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const a      = document.createElement('a');
    a.href       = URL.createObjectURL(blob);
    a.download   = `order_${(order.order_number || order.id).replace(/[^a-zA-Z0-9_-]/g, '')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const countByStatus = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  const displayRowsCount = displayRows.length;

  const handleBulkMatch = async (force = false) => {
    setBulkMatching(true);
    setBulkResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/supplies/bulk-auto-match-invoices?force=${force}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBulkResult(data);
      if (data.matched > 0) {
        toast.success(`Сверено: ${data.matched} из ${data.total} поставок`);
        fetchOrders();
      } else if (force) {
        toast.info(`Нет поставок для пересчёта (${data.total} обработано)`);
      } else {
        toast.info(`Не найдено подходящих накладных (${data.total} поставок без накладной)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка массовой сверки');
    } finally {
      setBulkMatching(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 shadow-sm px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => (window.location.href = '/')}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Назад
            </Button>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
              <Package className="text-white w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Заказы</h1>
              <p className="text-xs text-gray-400">
                {displayRowsCount} строк · {total} заказов
              </p>
            </div>

            {/* Status counters */}
            {Object.keys(countByStatus).length > 0 && (
              <div className="hidden md:flex items-center gap-2 ml-4">
                {Object.entries(countByStatus).map(([status, count]) => {
                  const s = STATUS_STYLE[status] || { badge: 'bg-gray-100 text-gray-600' };
                  return (
                    <button
                      key={status}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-opacity hover:opacity-75 ${s.badge}`}
                      onClick={() => setFilter('status', filters.status === status ? '' : status)}
                    >
                      {count} {status.toLowerCase()}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {user?.is_admin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  disabled={bulkMatching}
                  onClick={() => handleBulkMatch(false)}
                  title="Сверить все необработанные поставки с накладными"
                >
                  {bulkMatching ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Truck className="w-3.5 h-3.5" />
                  )}
                  {bulkMatching ? 'Сверяем...' : 'Сверить все'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50"
                  disabled={bulkMatching}
                  onClick={() => handleBulkMatch(true)}
                  title="Пересчитать сверку для всех поставок включая уже сверенные"
                >
                  {bulkMatching ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Truck className="w-3.5 h-3.5" />
                  )}
                  {bulkMatching ? 'Пересчитываем...' : 'Пересчитать все'}
                </Button>
              </>
            )}
            {user && (
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 max-w-5xl mx-auto space-y-4">
        {/* ── Filter bar ──────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <SlidersHorizontal className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700">Фильтры</span>
            {hasActiveFilters && (
              <button
                className="ml-auto flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
                onClick={clearFilters}
              >
                <X className="w-3.5 h-3.5" />
                Сбросить
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {/* General search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder={user?.is_admin ? 'Номер, клиент, email…' : 'Номер заказа…'}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-8 h-9 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
              {searchInput && (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchInput('')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Product search */}
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Товар / артикул…"
                value={productSearchInput}
                onChange={(e) => setProductSearchInput(e.target.value)}
                className="w-full pl-9 pr-8 h-9 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              />
              {productSearchInput && (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setProductSearchInput('')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Status */}
            <Select value={filters.status || 'all'} onValueChange={(v) => setFilter('status', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 text-sm border-gray-300">
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Supplier — admin only */}
            {user?.is_admin && suppliers.length > 0 && (
              <Select value={filters.supplier_id || 'all'} onValueChange={(v) => setFilter('supplier_id', v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm border-gray-300">
                  <SelectValue placeholder="Все поставщики" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все поставщики</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Invoice match filter — admin only */}
            {user?.is_admin && (
              <Select value={filters.invoice_filter || 'all'} onValueChange={(v) => setFilter('invoice_filter', v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm border-gray-300">
                  <SelectValue placeholder="Сверка накладной" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все сверки</SelectItem>
                  <SelectItem value="price">Расхождение цен</SelectItem>
                  <SelectItem value="items">Расхождение товаров</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Date from */}
            <div>
              <label className="block text-[11px] text-gray-400 mb-1 uppercase tracking-wide">С даты</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilter('date_from', e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
            </div>

            {/* Date to */}
            <div>
              <label className="block text-[11px] text-gray-400 mb-1 uppercase tracking-wide">По дату</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilter('date_to', e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
            </div>
          </div>
        </div>

        {/* ── Orders list ─────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm text-center py-24">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-gray-600 text-lg font-semibold">
              {hasActiveFilters ? 'Заказов не найдено' : 'Заказов пока нет'}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {hasActiveFilters
                ? 'Попробуйте изменить фильтры'
                : 'После оформления заказы появятся здесь'}
            </p>
            {hasActiveFilters ? (
              <Button className="mt-6" variant="outline" onClick={clearFilters}>
                Сбросить фильтры
              </Button>
            ) : (
              <Button className="mt-6" onClick={() => (window.location.href = '/')}>
                Перейти к каталогу
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-2.5">
              {displayRows.map((row) => {
                if (row.type === 'supply') {
                  return (
                    <SupplyGroupCard
                      key={row.key}
                      group={row}
                      isAdmin={user?.is_admin}
                      onStatusChange={handleStatusChange}
                      onSupplyStatusChange={handleSupplyStatusChange}
                      onSendInterkom={handleSendInterkom}
                      sendingId={sendingId}
                      updatingSupplyId={updatingSupplyId}
                      token={token}
                    />
                  );
                }
                return (
                  <OrderCard
                    key={row.order.id}
                    order={row.order}
                    isAdmin={user?.is_admin}
                    onStatusChange={handleStatusChange}
                    onSendInterkom={handleSendInterkom}
                    onExportCsv={handleExportCsv}
                    sendingId={sendingId}
                  />
                );
              })}
            </div>

            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPage={setPage}
              onPageSize={(s) => { setPageSize(s); setPage(1); }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
